/**
 * How the map is drawn: its colours, widths and the paint of its ports, as MapLibre expressions. Read by the browser's
 * map (public and admin) and by the share cards, which evaluate the same expressions on the server.
 */

// Base map styles, one per theme (the admin editor is always light)
const MAP_STYLE_URL = 'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json';
const MAP_STYLE_URL_DARK = 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json';

export type MapTheme = 'light' | 'dark';

/**
 * The share cards' basemap: OpenFreeMap's vector tiles (OpenStreetMap data in the OpenMapTiles schema; free, no key,
 * commercial use allowed), whose TileJSON names the week's tiles, and the credit their licence asks for.
 */
export const CARD_TILEJSON = 'https://tiles.openfreemap.org/planet';
export const CARD_TILES_CREDIT = '© OpenStreetMap';

export function mapStyleUrl(theme: MapTheme): string {
  return theme === 'dark' ? MAP_STYLE_URL_DARK : MAP_STYLE_URL;
}

// Custom layer colors (override base style)
const WATER_COLOR = '#cce5ff'; // Light blue
const WATER_COLOR_DARK = '#14202c'; // Deep blue, under --paper of the dark palette

/** The sea's colour in a theme, over the basemap's own. */
export function waterColor(theme: MapTheme): string {
  return theme === 'dark' ? WATER_COLOR_DARK : WATER_COLOR;
}

// Ferry route styling — the stylesheet's --accent of each theme, kept on these by the test of this file
const ROUTE_COLOR = '#005fd9';
const ROUTE_COLOR_DARK = '#6aa6ff';
/** The page's paper in a theme (`--paper` in styles.css): the casing under a route in dark mode, so a line stands off the basemap the way a port's white ring does, and a called port's fill. */
const PAPER = { light: '#ffffff', dark: '#14171c' } as const;
export const paperColor = (theme: MapTheme) => PAPER[theme];

/** A line no one sails any more (a ceased route page): grey, read as out of service on either basemap. */
const ROUTE_COLOR_CEASED = '#8a8f98';

/** The colour of a page's lines: the ferry blue of the theme, or grey on a ceased route page (`PageGeoJson.ceased`). */
export function routeColor(theme: MapTheme, ceased = false): string {
  if (ceased) return ROUTE_COLOR_CEASED;
  return theme === 'dark' ? ROUTE_COLOR_DARK : ROUTE_COLOR;
}

/**
 * Where the boats come from and go on to, beyond a page's trip: grey, as the stop list draws it, so the trip stands out
 * in blue. Measured against the water and the land of each basemap: at least 3:1 (WCAG's floor for graphics) at this
 * opacity, where the faint blue before it was under 2:1.
 */
const FAINT_LINE_COLOR = { light: '#5b6470', dark: '#8a8f98' } as const;
export const FAINT_LINE_OPACITY = 0.8;
/** A port beyond the trip (`kind` rest): grey, inside its white ring (3.9:1 against it, 5:1 against the dark basemap's land). */
export const PORT_COLOR_FAINT = '#7b828c';

/** The colour of the lines beyond a page's trip: grey, or the ceased grey on a ceased route page. */
export function faintColor(theme: MapTheme, ceased = false): string {
  return ceased ? ROUTE_COLOR_CEASED : FAINT_LINE_COLOR[theme];
}

export function routeCasingOpacity(theme: MapTheme): number {
  return theme === 'dark' ? 0.9 : 0;
}

/** What the mouse points at, a line or a port, on the map and in the panel (`--color-highlight` in styles.css): orange, off the blue. */
export const HIGHLIGHT_COLOR = '#f97316';
/** The highlight's outline, a hairline like every port's. */
export const HIGHLIGHT_OUTLINE = '#c2410c';
export const ROUTE_WIDTH = 3;
export const ROUTE_WIDTH_HOVER = 5;
/** A line's opacity in a theme. */
export const routeOpacity = (theme: MapTheme) => (theme === 'dark' ? 1 : 0.8);
/** A seasonal line's dashes, in line widths (the feature's `seasonal`: all its live services seasonal), drawn by its lanes. */
export const DASHES = [2, 1.5];
/** The width of the lines beyond a page's trip (`rest`), thinner than its own. */
export const REST_WIDTH = ROUTE_WIDTH - 1;

// Port color classes, by the sea a port's boats cross in a week (`PORT_SIZE`) — FlightConnections palette
export const PORT_COLOR_SMALL = '#D94030'; // Red — the rest, and a line sailed no week
export const PORT_COLOR_MEDIUM = '#F5B800'; // Amber/Gold — to 60.9%, or linking one port
export const PORT_COLOR_LARGE = '#3070E0'; // Cobalt blue — top 26.2%, or linking two
const PORT_COLOR_HUB = '#1B2A6B'; // Dark navy — top 3.4%, linking three or more
/** A hub's colour in a theme: the navy, white on the dark basemap where the navy all but disappears (`--logo` in styles.css). */
export const hubColor = (theme: MapTheme) => (theme === 'dark' ? '#ffffff' : PORT_COLOR_HUB);

/** Each size's outline: its colour, darker, a hairline as FlightConnections draws it, so a crowded coast stays calm. */
const PORT_OUTLINES = { small: '#9E2A1F', medium: '#A67C00', large: '#1F4FA8', hub: '#0B1440', faint: '#4b5058' };

/**
 * A port's radius by its size, small on the whole world (zoom 2) and growing as the map closes in on a coast (zoom 10):
 * FlightConnections' dots, which leave the map readable with a thousand ports on it. On a route page a port the trip
 * calls at on the way or beyond it (its `kind`) is drawn a small port's size, so the trip's ends stand out. `grow` adds
 * to every size, for a port under the mouse.
 */
const PORT_RADII = { small: [3, 5.5], medium: [3.5, 6.5], large: [4, 7.5], hub: [5, 9] } as const;
export function portRadius(grow = 0): unknown[] {
  const at = (stop: 0 | 1) => [
    'match',
    ['coalesce', ['get', 'kind'], ''],
    ['rest', 'call', 'near'],
    PORT_RADII.small[stop] + grow,
    [
      'match',
      ['get', 'color_class'],
      'hub',
      PORT_RADII.hub[stop] + grow,
      'large',
      PORT_RADII.large[stop] + grow,
      'medium',
      PORT_RADII.medium[stop] + grow,
      PORT_RADII.small[stop] + grow,
    ],
  ];
  return ['interpolate', ['linear'], ['zoom'], 2, at(0), 10, at(1)];
}

/**
 * Get MapLibre paint properties for a port circle layer with color coding.
 * Read by the public map, the admin editor and the share cards. On a route page a port's `kind` draws it as the stop list does: a port
 * the trip calls at on the way (`call`) a ring in the ferry blue on the paper colour, a port beyond the trip (`rest`)
 * grey, both a size smaller, so the trip's two ends (no `kind`) stand out in their size colour; a port page's ports nearby
 * (`near`) grey as well.
 */
export function getPortCirclePaint(theme: MapTheme = 'light', ceased = false): Record<string, unknown> {
  const kind = ['coalesce', ['get', 'kind'], ''];
  return {
    'circle-color': [
      'match',
      kind,
      ['rest', 'near'],
      PORT_COLOR_FAINT,
      'call',
      paperColor(theme),
      [
        'match',
        ['get', 'color_class'],
        'small',
        PORT_COLOR_SMALL,
        'medium',
        PORT_COLOR_MEDIUM,
        'large',
        PORT_COLOR_LARGE,
        'hub',
        hubColor(theme),
        ROUTE_COLOR, // fallback for ports without color_class
      ],
    ],
    'circle-radius': portRadius(),
    'circle-stroke-color': [
      'match',
      kind,
      'call',
      routeColor(theme, ceased),
      ['rest', 'near'],
      PORT_OUTLINES.faint,
      [
        'match',
        ['get', 'color_class'],
        'hub',
        PORT_OUTLINES.hub,
        'large',
        PORT_OUTLINES.large,
        'medium',
        PORT_OUTLINES.medium,
        PORT_OUTLINES.small,
      ],
    ],
    // A call's ring wide enough to read as one; every other port a hairline
    'circle-stroke-width': ['match', kind, 'call', 1.5, 1],
  };
}

/**
 * Sort priority for port color classes — higher value = rendered on top / preferred on click.
 * Used by both MapLibre circle-sort-key and the pick-best-port helper.
 */
const PORT_SORT_PRIORITY: Record<string, number> = { hub: 4, large: 3, medium: 2, small: 1 };
const PORT_SORT_DEFAULT = 1;

/**
 * Get MapLibre layout properties for a port circle layer (sort hubs on top).
 */
export function getPortCircleLayout(): Record<string, unknown> {
  return {
    'circle-sort-key': ['match', ['get', 'color_class'], ...Object.entries(PORT_SORT_PRIORITY).flat(), PORT_SORT_DEFAULT],
  };
}
